import type {
  CreateEmailRequest,
  CreateEmailResponse,
  Email,
  UpdateEmailRequest,
  UpdateEmailResponse,
} from '@compass/client/types/email';
import { emailService } from '@services/api';
import { useStore } from '@store/store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

// Sort emails by created_at in ascending order (oldest first)
const sortEmailsByCreatedAt = (emails: Email[]): Email[] => {
  return [...emails].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });
};

export const useCaseRecordEmails = (caseRecordId: string | null) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const isEnabled = useMemo(() => {
    return !!caseRecordId && !!selectedOrganizationId && !!selectedProgramId;
  }, [caseRecordId, selectedOrganizationId, selectedProgramId]);

  return useQuery({
    queryKey: ['caseRecordEmails', caseRecordId],
    queryFn: async () => {
      try {
        const response = await emailService.listEmailsByCaseId({
          organization_id: selectedOrganizationId!,
          program_id: selectedProgramId!,
          case_id: caseRecordId!,
        });

        // Return new object with sorted emails if they exist
        if (response.emails && response.emails.length > 0) {
          return {
            ...response,
            emails: sortEmailsByCreatedAt(response.emails),
          };
        }

        return response;
      } catch (error) {
        // Return empty emails array for 404 (no emails found) to avoid breaking UI
        if (error instanceof AxiosError && error.response?.status === 404) {
          return { emails: [] };
        }
        throw error;
      }
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new email
 */
export const useCreateEmail = () => {
  const queryClient = useQueryClient();
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation<
    CreateEmailResponse,
    Error,
    { caseRecordId: string; data: CreateEmailRequest }
  >({
    mutationFn: async ({ caseRecordId, data }) => {
      if (testModeEnabled) {
        throw new Error('Emails are not supported in test mode');
      }
      return emailService.createEmail({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_id: caseRecordId,
        ...data,
      });
    },
    onSuccess: (_data, variables) => {
      // Invalidate emails list to include new email
      queryClient.invalidateQueries({
        queryKey: ['caseRecordEmails', variables.caseRecordId],
      });
    },
  });
};

/**
 * Hook to update an email
 */
export const useUpdateEmail = () => {
  const queryClient = useQueryClient();
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation<
    UpdateEmailResponse,
    Error,
    {
      caseRecordId: string;
      emailId: string;
      data: Omit<UpdateEmailRequest, 'organization_id' | 'program_id' | 'case_id' | 'id'>;
    },
    { previousEmail: unknown }
  >({
    mutationFn: async ({ caseRecordId, emailId, data }) => {
      if (testModeEnabled) {
        throw new Error('Emails are not supported in test mode');
      }
      return emailService.updateEmail({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_id: caseRecordId,
        id: emailId,
        ...data,
      });
    },
    onMutate: async ({ caseRecordId, emailId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['caseRecordEmails', caseRecordId] });

      // Snapshot the previous value
      const previousEmail = queryClient.getQueryData(['caseRecordEmails', caseRecordId]);

      // Optimistically update the emails list
      queryClient.setQueryData(['caseRecordEmails', caseRecordId], (old: any) => {
        if (!old?.emails) return old;
        return {
          ...old,
          emails: old.emails.map((email: Email) =>
            email.id === emailId ? { ...email, ...data } : email,
          ),
        };
      });

      return { previousEmail };
    },
    onError: (_err, variables, context) => {
      // Roll back on error
      if (context?.previousEmail) {
        queryClient.setQueryData(
          ['caseRecordEmails', variables.caseRecordId],
          context.previousEmail,
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: ['caseRecordEmails', variables.caseRecordId],
      });
    },
  });
};
